import { Component, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { constants } from '@constants/constants';
import { isPeri } from '@extensions/extensions';
import { MobileFormControl } from '@form/mobile-form-control/mobile-form-control';
import { NameWithDigitsFormControl } from '@form/name-with-digits-form-control/name-with-digits-form-control';
import { AlertModalComponent, modalTypes } from '@modals/alert-modal/alert-modal.component';
import { ProfileStepName } from '@models/profile-step-name';
import { LabelTextPipe } from '@pipes/label-text.pipe';
import { ProgressBarService } from '@services/progress-bar.service';
import { pushGtmTag } from '@shared/extensions/gtm.extentions';
import { ProfileStepCode } from '@shared/models/profile-step-code';
import {
  DocumentSignatureRequest,
  ExternalServices,
  Profile,
  RetrySigningDocumentRequest,
  SignatureTransactionRequest,
  SignatureTransactionResponse
} from '@webapi/MIF.Subscription.WebApi';
import { CustomerService } from '@webapi/services/customer.service';
import { SettingsService } from '@webapi/services/settings.service';
import { WsDqeService } from '@webapi/services/ws-dqe.service';
import { BsModalService } from 'ngx-bootstrap/modal';

@Component({
  standalone: false,
  selector: 'app-phone-confirmation',
  templateUrl: './phone-confirmation.component.html',
  styleUrls: ['./phone-confirmation.component.scss']
})
export class PhoneConfirmationComponent implements OnInit {
  public form: FormGroup;
  public getSmsText = 'Envoyer le code par SMS';
  public smsRequested = false;
  public pdfContent: string;
  public signatureTransactionRequest: SignatureTransactionRequest;
  public userId: string;
  public clientProfile: Profile;
  public externalServicesConfiguration: ExternalServices;
  public ProfileStepName = ProfileStepName;
  public isPeri = isPeri();
  public signatoryId: string;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private customerService: CustomerService,
    private progressBarService: ProgressBarService,
    private bsModalService: BsModalService,
    private wsDqeService: WsDqeService,
    private settingsService: SettingsService,
    private labelTextPipe: LabelTextPipe
  ) {}

  async ngOnInit(): Promise<void> {
    this.progressBarService.sendStep(constants.steps.signature.phoneConfirmation.initialStep);
    this.externalServicesConfiguration = await this.settingsService.getExternalServicesConfiguration();
    this.pdfContent = history.state.data;
    this.userId = this.route.snapshot.queryParamMap.get('userId');

    if (!this.pdfContent) {
      // the end-user should always come first from the Signature page
      this.router.navigate(['signature'], {
        queryParams: { userId: this.customerService.userId }
      });
    }

    this.form = new FormGroup({
      mobilePhone: new MobileFormControl(true, true, {
        readOnlyCss: true,
        hideErrorMark: true,
        hideSuccessMark: true
      }),
      sms: new NameWithDigitsFormControl(true, false, {
        capitalizeAll: true,
        minLength: 6,
        maxLength: 6,
        requiredErrorMessage: this.labelTextPipe.transform('SIGNATURE.SMSMessageCode') // todo: use the same label as in the proveance app
      })
    });

    await this.fetchInitData();
  }

  async fetchInitData(): Promise<void> {
    this.clientProfile = await this.customerService.getUserData();

    if (this.clientProfile.contact.mobilePhone) {
      this.form.get('mobilePhone').patchValue(this.clientProfile.contact.mobilePhone);
    }
  }

  async getSms($event: any): Promise<void> {
    $event.preventDefault();
    const mobilePhone = this.clientProfile.contact.mobilePhone;
    let mobileInValid = false;

    if (mobilePhone && mobilePhone !== '' && this.externalServicesConfiguration.dqeValidation === true) {
      mobileInValid = !(await this.wsDqeService.validatePhoneNumber(mobilePhone));

      if (mobileInValid) {
        this.form.get('mobilePhone').setErrors({ phoneRemoteValidator: true });
      }
    }

    if (!mobileInValid) {
      this.processSignDocument();
    }
  }

  async processSignDocument(): Promise<void> {
    const mobilePhone = this.clientProfile.contact.mobilePhone;

    if (!this.signatoryId) {
      // process first time
      this.getSmsText = this.labelTextPipe.transform('SIGNATURE.SendCodeButtonAgain');
      this.smsRequested = true;

      const singatureRequest = new DocumentSignatureRequest({
        profileId: this.userId,
        phoneNumber: `+33${mobilePhone}`,
        htmlContent: this.pdfContent
      });

      const signatureTransactionResponse = (await this.customerService.signDocument(
        singatureRequest
      )) as SignatureTransactionResponse;

      this.signatoryId = signatureTransactionResponse.signatoryId;

      this.signatureTransactionRequest = new SignatureTransactionRequest({
        profileId: this.userId,
        transactionId: signatureTransactionResponse.transactionId,
        signatoryId: signatureTransactionResponse.signatoryId,
        validationCode: null
      });
    } else {
      // process reSignDoc not the first time
      const retrySigningDocumentRequestModel = new RetrySigningDocumentRequest({
        profileId: this.userId,
        signatureId: this.signatoryId,
        phoneNumber: `+33${mobilePhone}`
      });

      this.customerService.retrySigningDocument(retrySigningDocumentRequestModel);
      // do noting, cause signatureTransactionRequest already prefilled
    }
  }

  goToMyProfile(): void {
    this.router.navigate(['my-details/contacts'], {
      queryParams: { userId: this.customerService.userId },
      state: { scrollToPhones: true }
    });
  }

  async onSubmit($event: any): Promise<void> {
    $event.preventDefault();
    const sms = this.form.get('sms').value.toString();
    this.signatureTransactionRequest.validationCode = sms;
    const signatureTransactionResponse = await this.customerService.validateTransaction(
      this.signatureTransactionRequest
    );

    if (signatureTransactionResponse.apiError !== undefined && signatureTransactionResponse.apiError !== '') {
      this.bsModalService.show(AlertModalComponent, {
        initialState: {
          type: modalTypes.error,
          title: this.labelTextPipe.transform('SIGNATURE.WrongCodeText')
        }
      });
      this.form.get('sms').patchValue('');
    } else {
      pushGtmTag(ProfileStepCode.PhoneConfirmation);

      this.router.navigate(['success'], {
        queryParams: { userId: this.customerService.userId },
        state: { data: signatureTransactionResponse }
      });
    }
  }
}
